import React, { useEffect, useMemo, useState } from "react";
import { MapPin, Navigation, Home, Loader2 } from "lucide-react";
import { suggestPlaces } from "@/lib/stayflowApi";
import { DEFAULT_PLACES, newPlaceSession } from "@/lib/places";

// One Google call per typing pause, from 3 letters. Stay-name matches are local.
export const useWhereSuggestions = (query, stays = []) => {
  const [session, setSession] = useState(() => newPlaceSession());
  const [places, setPlaces] = useState([]);
  const [capped, setCapped] = useState(false);
  const [loading, setLoading] = useState(false);
  const q = (query || "").trim();
  const typing = q.length >= 3;

  useEffect(() => {
    if (!typing) { setPlaces([]); setLoading(false); return; }
    let dead = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await suggestPlaces(q, session);
        if (dead) return;
        setPlaces(r.places || []);
        setCapped(!!r.capped);
      } catch {
        if (!dead) setPlaces([]);
      } finally {
        if (!dead) setLoading(false);
      }
    }, 300);
    return () => { dead = true; clearTimeout(t); };
  }, [q, typing, session]);

  const stayMatches = useMemo(() => {
    if (!typing) return [];
    const n = q.toLowerCase();
    return stays.filter((s) => (s.name || "").toLowerCase().includes(n)).slice(0, 4);
  }, [q, typing, stays]);

  return { stayMatches, places, capped, loading, typing, session, resetSession: () => setSession(newPlaceSession()) };
};
const Row = ({ icon, title, sub, onClick, testid, big }) => (
  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick} data-testid={testid}
    className="w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-brand-soft active:scale-[0.99]">
    {icon}
    <span className="min-w-0">
      <span className={`block font-medium text-ink truncate ${big ? "font-heading font-bold" : ""}`}>{title}</span>
      {sub && <span className="block text-xs text-ink-soft truncate">{sub}</span>}
    </span>
  </button>
);

const Head = ({ children }) => (
  <p className="px-4 pt-3 pb-1.5 text-[0.68rem] font-semibold uppercase tracking-[0.12em] text-ink-faint">{children}</p>
);

export const WhereSuggestions = ({ query, stays = [], onPickPlace, onPickStay, onNearMe, locating, big = false }) => {
  const { stayMatches, places, loading, typing, capped } = useWhereSuggestions(query, stays);
  // Over the daily Google limit: stays + the six regulars still work.
  const showDefaults = !typing || (capped && places.length === 0);

  return (
    <div data-testid="where-suggestions">
      {!typing ? (
        <>
          {onNearMe && (
            <Row testid="where-nearby" big={big}
              icon={locating ? <Loader2 className="w-4 h-4 text-brand animate-spin shrink-0" /> : <Navigation className="w-4 h-4 text-brand shrink-0" />}
              title="Nearby" sub="Find what's around you" onClick={onNearMe} />
          )}
          <Head>Popular destinations</Head>
          {DEFAULT_PLACES.map((d) => (
            <Row key={d.place_id} testid={`where-place-${d.main}`} big={big}
              icon={<MapPin className="w-4 h-4 text-brand shrink-0" />}
              title={d.main} sub={d.secondary} onClick={() => onPickPlace(d)} />
          ))}
        </>
      ) : (
        <>
          {stayMatches.length > 0 && (
            <>
              <Head>Stays</Head>
              {stayMatches.map((s) => (
                <Row key={s.id} testid={`where-stay-${s.id}`} big={big}
                  icon={<Home className="w-4 h-4 text-ink shrink-0" />}
                  title={s.name} sub="StayFlow stay" onClick={() => onPickStay(s)} />
              ))}
            </>
          )}
          <Head>Places{loading ? " · searching…" : ""}</Head>
          {places.length === 0 && !loading && (
            <p className="px-4 pb-2 text-sm text-ink-soft" data-testid="where-places-none">
              {capped ? "Place search is resting — pick a popular destination below." : "No matching places."}
            </p>
          )}
          {places.map((p) => (
            <Row key={p.place_id} testid={`where-place-${p.main}`} big={big}
              icon={<MapPin className="w-4 h-4 text-brand shrink-0" />}
              title={p.main} sub={p.secondary} onClick={() => onPickPlace(p)} />
          ))}
          {places.length > 0 && (
            <p className="px-4 pb-2.5 pt-1 text-[11px] text-ink-faint" data-testid="where-google-attribution">Places by Google Maps</p>
          )}
          {showDefaults && DEFAULT_PLACES.map((d) => (
            <Row key={d.place_id} testid={`where-place-${d.main}`} big={big}
              icon={<MapPin className="w-4 h-4 text-brand shrink-0" />}
              title={d.main} sub={d.secondary} onClick={() => onPickPlace(d)} />
          ))}
          {onNearMe && (
            <Row testid="where-nearby" big={big}
              icon={locating ? <Loader2 className="w-4 h-4 text-brand animate-spin shrink-0" /> : <Navigation className="w-4 h-4 text-brand shrink-0" />}
              title="Nearby" sub="Find what's around you" onClick={onNearMe} />
          )}
        </>
      )}
    </div>
  );
};

export default WhereSuggestions;
